import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Camera, Check, FileImage, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { compressImage } from '../../lib/image';

/**
 * Modal for attaching a document image to a person. Opens the device
 * camera or photo roll, compresses to a 1600px-max JPEG, then captures
 * a name + optional description before posting one DocumentAttached
 * event.
 *
 * Documents tend to be photographed at distance (licence cards, certs)
 * so the resize cap is bigger than reception photos. Quality stays at
 * 0.85 — enough for the text on the card to remain legible without
 * shipping multi-MB uploads.
 */
export function AddDocumentModal({
  open,
  personName,
  onClose,
  onSave,
}: {
  open: boolean;
  personName: string;
  onClose: () => void;
  onSave: (input: {
    name: string;
    description?: string;
    dataUrl: string;
  }) => void | Promise<void>;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setPreview(null);
      setError(null);
      setProcessing(false);
      setSaving(false);
      return;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  async function handleFile(file: File) {
    setError(null);
    setProcessing(true);
    try {
      const dataUrl = await compressImage(file, 1600, 0.85);
      setPreview(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the image');
    } finally {
      setProcessing(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!preview) {
      setError('Take or pick a photo first');
      return;
    }
    if (!name.trim()) {
      setError('Give the document a name');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        dataUrl: preview,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the document');
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-document-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <form
        onSubmit={submit}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg"
      >
        <header className="flex items-start justify-between gap-3 border-b border-divider/10 px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
              <FileImage className="h-3.5 w-3.5" />
              <span>Document</span>
            </div>
            <h2 id="add-document-title" className="mt-1 text-lg font-semibold tracking-tight text-fg">
              Attach to {personName}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            Close
          </Button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto bg-surface-2 p-4 sm:p-5">
          {preview ? (
            <div className="overflow-hidden rounded-token-sm border border-line/10 bg-black">
              <img
                src={preview}
                alt="Document preview"
                className="mx-auto block max-h-64 w-auto"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={processing}
              className="flex h-48 w-full flex-col items-center justify-center gap-2 rounded-token-sm border-2 border-dashed border-line-strong/30 bg-surface text-fg-muted transition-colors hover:border-accent/50 hover:bg-accent/5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Camera className="h-8 w-8 text-fg-dim" />
              <span className="text-sm font-medium">
                {processing ? 'Loading image...' : 'Tap to take a photo'}
              </span>
              <span className="text-xs text-fg-dim">
                Or pick an existing photo
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />

          {preview ? (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() => fileInputRef.current?.click()}
                disabled={saving || processing}
              >
                Retake
              </Button>
            </div>
          ) : null}

          <label className="block text-sm">
            <span className="font-medium text-fg">Name</span>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Driving licence (front)"
              className="mt-1.5 block h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg">
              Description <span className="font-normal text-fg-dim">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Expires 2029, class C+E"
              className="mt-1.5 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>

          {error ? (
            <div className="rounded-token-sm border border-danger/30 bg-danger-soft/15 px-3 py-2 text-xs text-danger-soft-fg">
              {error}
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="secondary" size="md" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            iconLeft={<Check className="h-4 w-4" />}
            loading={saving}
            disabled={!preview || processing}
          >
            Save document
          </Button>
        </footer>
      </form>
    </div>
  );
}

